import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Row, Segmented, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import { ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import tmAdminExtService from '../../services/tmAdminExtService';
import { TypeTag, fmtNum, fmtTime, toastErr } from './common';
import { StreamQuickActions, StreamRef, pct } from './opsCommon';
import StreamOpsDrawer from './StreamOpsDrawer';

const { Text } = Typography;

const PERIODS = [{ value: 'today', label: '今天' }, { value: '7d', label: '7 天' }, { value: '30d', label: '30 天' }];
const age = (s) => {
    if (s === null || s === undefined) return '-';
    if (s < 120) return `${s}s`;
    if (s < 7200) return `${Math.round(s / 60)}m`;
    return `${Math.round(s / 3600)}h`;
};

const Totals = ({ t }) => {
    if (!t) return null;
    const warn = (v, lim) => (v !== null && v > lim ? '#cf1322' : undefined);
    const items = [
        ['接收', t.accepted], ['送达', t.delivered, pct(t.rates.delivered, 1)], ['延迟中', t.deferred],
        ['硬退', t.bounced_hard, pct(t.rates.hard_bounce), warn(t.rates.hard_bounce, 0.05)], ['软退', t.bounced_soft, pct(t.rates.soft_bounce)],
        ['投诉', t.complained, pct(t.rates.complaint, 3), warn(t.rates.complaint, 0.001)], ['拒收', t.rejected, pct(t.rates.rejected)],
        ['病毒', t.virus, null, t.virus ? '#cf1322' : undefined], ['垃圾拒收', t.spam_rejected, null, t.spam_rejected ? '#d46b08' : undefined],
    ];
    return (
        <Row gutter={[12, 12]}>
            {items.map(([label, v, sub, color]) => (
                <Col key={label} xs={12} sm={8} md={6} lg={4} xl={3}>
                    <Statistic title={sub ? <span>{label} <Text type="secondary" style={{ fontSize: 12 }}>{sub}</Text></span> : label}
                        value={v ?? 0} valueStyle={{ fontSize: 20, color }} />
                </Col>
            ))}
        </Row>
    );
};

const Overview = () => {
    const [d, setD] = useState(null);
    const [period, setPeriod] = useState('7d');
    const [loading, setLoading] = useState(false);
    const [probing, setProbing] = useState(false);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(async (probe = false) => {
        probe ? setProbing(true) : setLoading(true);
        try {
            const r = await tmAdminExtService.overview({ period, probe: probe ? 1 : undefined });
            setD(r?.data || null);
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setLoading(false);
            setProbing(false);
        }
    }, [period]);

    useEffect(() => { load(); }, [load]);

    const t = d?.totals?.[period];

    const nodeCols = [
        { title: '节点', key: 'n', render: (_, n) => <span>#{n.id} {n.name} <Text type="secondary" style={{ fontSize: 12 }}>{n.ip}</Text></span> },
        { title: '状态', dataIndex: 'status', width: 90, render: (v) => <Tag color={v === 'active' ? 'green' : v === 'draining' ? 'orange' : 'default'}>{v}</Tag> },
        { title: '队列', dataIndex: 'queue_depth', width: 80, render: fmtNum },
        { title: '最老', dataIndex: 'oldest_age_s', width: 80, render: (v) => <span style={{ color: v > 3600 ? '#cf1322' : undefined }}>{age(v)}</span> },
        {
            title: '同步', key: 's', width: 160,
            render: (_, n) => <Space size={4}>{n.healthy ? <Tag color="green">{age(n.sync_age_s)}</Tag> : <Tag color="red">{age(n.sync_age_s)}</Tag>}{n.sync_lag_versions ? <Text type="secondary" style={{ fontSize: 12 }}>落后 {n.sync_lag_versions}</Text> : null}</Space>,
        },
        { title: '检测', key: 'h', width: 140, render: (_, n) => (n.health_error ? <Tooltip title={n.health_error}><Tag color="red">失败</Tag></Tooltip> : <Text type="secondary" style={{ fontSize: 12 }}>{age(n.health_age_s)} 前</Text>) },
    ];

    const topCols = [
        { title: '发信流', key: 's', render: (_, r) => <StreamRef stream={r.stream} onOpen={setOpenId} /> },
        { title: '用户', key: 'u', render: (_, r) => (r.user ? <span>#{r.user.id} {r.user.username}<br /><Text type="secondary" style={{ fontSize: 12 }}>{r.user.email}</Text></span> : '-') },
        { title: '类型', key: 't', width: 70, render: (_, r) => (r.stream?.type ? <TypeTag type={r.stream.type} /> : '-') },
        { title: '套餐', key: 'p', render: (_, r) => r.plan?.name || '-' },
        { title: '发送', dataIndex: 'total', width: 90, render: fmtNum, sorter: (a, b) => a.total - b.total, defaultSortOrder: 'descend' },
        { title: '硬退', key: 'h', width: 90, render: (_, r) => <span style={{ color: r.rates.hard_bounce > 0.05 ? '#cf1322' : undefined }}>{pct(r.rates.hard_bounce)}</span>, sorter: (a, b) => (a.rates.hard_bounce || 0) - (b.rates.hard_bounce || 0) },
        { title: '投诉', key: 'c', width: 90, render: (_, r) => <span style={{ color: r.rates.complaint > 0.001 ? '#cf1322' : undefined }}>{pct(r.rates.complaint, 3)}</span>, sorter: (a, b) => (a.rates.complaint || 0) - (b.rates.complaint || 0) },
        { title: '拒收', key: 'r', width: 80, render: (_, r) => pct(r.rates.rejected, 1) },
        { title: '病毒/垃圾', key: 'v', width: 90, render: (_, r) => `${r.virus} / ${r.spam_rejected}` },
    ];

    const nearCols = [
        { title: '发信流', key: 's', render: (_, r) => <StreamRef stream={r.stream} onOpen={setOpenId} /> },
        { title: '用户', key: 'u', render: (_, r) => (r.user ? `#${r.user.id} ${r.user.username || ''}` : '-') },
        { title: '24h 发送', dataIndex: 'sent_24h', width: 90, render: (v, r) => <Tooltip title={r.volume_gate_reached ? null : `未到判定量 ${r.thresholds.min_volume}`}>{fmtNum(v)}{r.volume_gate_reached ? '' : ' *'}</Tooltip> },
        { title: '硬退/阈值', key: 'h', width: 100, render: (_, r) => pct(r.of_threshold.hard_bounce, 0) },
        { title: '投诉/阈值', key: 'c', width: 100, render: (_, r) => pct(r.of_threshold.complaint, 0) },
        { title: '垃圾/阈值', key: 'sp', width: 100, render: (_, r) => pct(r.of_threshold.spam, 0) },
        { title: '', key: 'op', width: 150, render: (_, r) => <StreamQuickActions stream={r.stream} onDone={() => load()} /> },
    ];

    const signupCols = [
        { title: '用户', key: 'u', render: (_, r) => <span>#{r.user.id} {r.user.username} {r.user.email_verified ? null : <Tag>邮箱未验证</Tag>}<br /><Text type="secondary" style={{ fontSize: 12 }}>{r.user.email}</Text></span> },
        { title: '注册', key: 'c', width: 150, render: (_, r) => fmtTime(r.user.created_at) },
        { title: '发信流', dataIndex: 'streams', width: 80, render: (v, r) => <span>{v}{r.sandbox ? <Tag color="gold" style={{ marginInlineStart: 4 }}>沙箱</Tag> : null}</span> },
        { title: '首封', key: 'f', render: (_, r) => (r.first_send ? <span>{fmtTime(r.first_send.at)} <Text type="secondary" style={{ fontSize: 12 }}>注册后 {age(r.first_send.after_signup_s)} · {r.first_send.rcpt} · {r.first_send.status}</Text></span> : <Text type="secondary">未发送</Text>) },
        { title: '7 天', dataIndex: 'sent_7d', width: 80, render: fmtNum },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card title="邮件推送总览" loading={loading && !d} extra={
                <Space>
                    <Segmented options={PERIODS} value={period} onChange={setPeriod} />
                    <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading} />
                </Space>
            }>
                <Totals t={t} />
                {d && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        发信流 {Object.entries(d.streams || {}).map(([k, n]) => `${k} ${n}`).join(' · ')} · 沙箱 {d.sandbox_streams} · 同步版本 v{d.sync_version} · {fmtTime(d.generated_at)}
                    </Text>
                )}
            </Card>
            <Card size="small" title="发信节点" extra={<Button size="small" icon={<ThunderboltOutlined />} loading={probing} onClick={() => load(true)}>实时检测</Button>}>
                <Table rowKey="id" size="small" pagination={false} dataSource={d?.nodes || []} columns={nodeCols} scroll={{ x: 700 }} />
            </Card>
            <Card size="small" title={`接近自动暂停（24h ≥ 60%）`}>
                <Table rowKey="stream_id" size="small" pagination={false} dataSource={d?.near_thresholds || []} columns={nearCols} scroll={{ x: 800 }}
                    locale={{ emptyText: '无' }} />
            </Card>
            <Card size="small" title={`发送量 Top 20（${PERIODS.find((p) => p.value === period)?.label}）`}>
                <Table rowKey={(r) => r.stream?.id} size="small" pagination={false} dataSource={d?.top_senders || []} columns={topCols} scroll={{ x: 1000 }} />
            </Card>
            <Card size="small" title="新注册用户（14 天）">
                <Table rowKey={(r) => r.user.id} size="small" pagination={{ pageSize: 10, hideOnSinglePage: true }} dataSource={d?.recent_signups || []} columns={signupCols} scroll={{ x: 800 }} />
            </Card>
            <StreamOpsDrawer streamId={openId} onClose={() => setOpenId(null)} onChanged={() => load()} />
        </Space>
    );
};

export default Overview;
